import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useCompositionScope } from './useExecutionCompositions.ts';
import { AUTOMATIONS_KEY, SUITE_KEYS } from '@/utils/queryKeys.ts';
import type {
  ActivationFence,
  ActivationHistoryItem,
  DirectRestorePublicationPreview,
  DirectRestorePublicationResult,
  LegacyActivationAudit,
  PublicationConfirmation,
  PublicationPreview,
  ReleasePublicationResult,
  PublicationIntegrity,
  EditorPublicationSummary,
  DirectVersionPublicationRequest,
  DirectVersionPublicationResult,
} from '@/types/automationPublications.ts';
import type { ReleaseFoundation } from '@/types/automationReleases.ts';

export function useEditorPublicationSummary(automationId?: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'editor-publication-summary', automationId],
    enabled: Boolean(automationId && scope[3]),
    retry: false,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<EditorPublicationSummary>(
        `/automations/${automationId}/publication-summary`,
        { signal },
      );
      if (!data || data.automation_id !== automationId)
        throw new Error('Publication summary does not match this Automation.');
      return data;
    },
  });
}

export function usePublicationIntegrity(
  automationId?: string,
  enabled = true,
  triggerStepId?: string,
) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'publication-integrity', automationId, triggerStepId ?? null],
    enabled: Boolean(enabled && automationId && scope[3]),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always' as const,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<PublicationIntegrity>(
        `/automations/${automationId}/publication-integrity`,
        {
          signal,
          params: triggerStepId === undefined ? {} : { trigger_step_id: triggerStepId },
        },
      );
      if (
        !data ||
        data.automation_id !== automationId ||
        data.project_id !== scope[3] ||
        (data.trigger_step_id ?? null) !== (triggerStepId ?? null) ||
        !Number.isInteger(data.head_revision) ||
        data.head_revision < 0 ||
        ![data.published_release_id, data.published_version_id].every(
          (id) => id === null || (typeof id === 'string' && id.length > 0),
        ) ||
        ![
          'healthy',
          'invalid',
          'restricted',
          'stale',
          'unpublished',
          'rolled_back',
          'legacy_adoption_required',
        ].includes(data.status) ||
        !Array.isArray(data.findings) ||
        data.findings.some(
          (finding) =>
            !finding || typeof finding.code !== 'string' || typeof finding.message !== 'string',
        ) ||
        (data.status === 'healthy' && (!data.published_version_id || data.findings.length > 0))
      )
        throw new Error('Publication integrity does not confirm the requested Automation.');
      return data;
    },
  });
}

export function useAutomationActivation(automationId?: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'activation', automationId],
    enabled: Boolean(automationId && scope[3]),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always' as const,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ActivationFence>(`/automations/${automationId}/activation`, {
        signal,
      });
      if (
        data.automation_id !== automationId ||
        !Array.isArray(data.active_roots) ||
        !Number.isInteger(data.head_revision) ||
        data.head_revision < 0 ||
        data.active_roots.some((root) => root.automation_id !== automationId) ||
        Boolean(data.published_version_id) !== Boolean(data.active_roots.length)
      )
        throw new Error('Activation does not confirm the requested Automation.');
      return data;
    },
  });
}
export function useActivationHistory(automationId: string, beforeRevision?: number) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'activation-history', automationId, beforeRevision],
    enabled: Boolean(automationId && scope[3]),
    retry: false,
    staleTime: 0,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ActivationHistoryItem[]>(
        `/automations/${automationId}/activation-history`,
        {
          signal,
          params: beforeRevision === undefined ? {} : { before_revision: beforeRevision },
        },
      );
      if (
        !Array.isArray(data) ||
        data.some((item) => item.roots.some((root) => root.automation_id !== automationId))
      )
        throw new Error('Publication history belongs to another Automation.');
      return data;
    },
  });
}
export function useLegacyActivationAudit(automationId: string, enabled: boolean) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'legacy-audit', automationId],
    enabled: Boolean(enabled && automationId && scope[3]),
    retry: false,
    staleTime: 0,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<LegacyActivationAudit>(
        `/automations/${automationId}/legacy-activation-audit`,
        { signal },
      );
      if (data.automation_id !== automationId)
        throw new Error('Adoption audit belongs to another Automation.');
      return data;
    },
  });
}
export function usePublicationMutations() {
  const api = useApiClient();
  const client = useQueryClient();
  const scope = useCompositionScope();
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: scope }),
      client.invalidateQueries({ queryKey: AUTOMATIONS_KEY }),
      client.invalidateQueries({ queryKey: SUITE_KEYS.root(scope[2], scope[3], scope[1]) }),
    ]);
  };
  const freeze = useMutation({
    retry: false,
    mutationFn: async ({ releaseId, revision }: { releaseId: string; revision: number }) => {
      const { data } = await api.post<ReleaseFoundation>(
        `/automation-releases/${releaseId}/freeze`,
        { expected_lifecycle_revision: revision },
      );
      if (data.id !== releaseId) throw new Error('Freeze returned another Release.');
      return data;
    },
    onSuccess: refresh,
  });
  const preview = useMutation({
    retry: false,
    mutationFn: async ({
      releaseId,
      automationIds,
      action,
    }: {
      releaseId: string;
      automationIds: string[];
      action: 'activate' | 'rollback';
    }) => {
      const { data } = await api.post<PublicationPreview>(
        `/automation-releases/${releaseId}/activation-preview`,
        {
          automation_ids: automationIds,
          action,
        },
      );
      const targets = data.plan?.targets?.map((target) => target.before.automation_id) ?? [];
      if (
        data.plan?.schema_version !== 'publication_plan.v2' ||
        data.plan.release?.release_id !== releaseId ||
        data.plan.action !== action ||
        targets.length !== automationIds.length ||
        new Set(targets).size !== targets.length ||
        targets.some((id) => !automationIds.includes(id)) ||
        !data.preview_token ||
        !data.preview_digest ||
        !Number.isFinite(Date.parse(data.plan.expires_at))
      )
        throw new Error('Publication preview does not confirm the reviewed targets.');
      return data;
    },
  });
  const confirm = useMutation({
    retry: false,
    mutationFn: async (request: PublicationConfirmation) => {
      const { data } = await api.post<ReleasePublicationResult>(
        '/automation-publications/confirm',
        request,
      );
      if (
        !data.publication_id ||
        !data.release_id ||
        !Array.isArray(data.activations) ||
        !data.activations.length
      )
        throw new Error('Publication outcome is unconfirmed. Retry the identical request.');
      return data;
    },
    onSuccess: refresh,
  });
  return { freeze, preview, confirm };
}

export function useDirectRestoreConfirmation() {
  const api = useApiClient();
  const client = useQueryClient();
  const scope = useCompositionScope();
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: scope }),
      client.invalidateQueries({ queryKey: AUTOMATIONS_KEY }),
      client.invalidateQueries({ queryKey: SUITE_KEYS.root(scope[2], scope[3], scope[1]) }),
    ]);
  };
  return useMutation({
    retry: false,
    mutationFn: async (request: PublicationConfirmation) => {
      const { data } = await api.post<DirectRestorePublicationResult>(
        '/automation-publications/confirm',
        request,
      );
      if (
        !data.publication_id ||
        data.release_id !== null ||
        !Array.isArray(data.activations) ||
        data.activations.length !== 1
      )
        throw new Error('Restore outcome is unconfirmed. Retry the identical request.');
      return data;
    },
    onSuccess: refresh,
  });
}

export function useRestorePreviousLiveState() {
  const api = useApiClient();
  return useMutation({
    retry: false,
    mutationFn: async ({
      automationId,
      expectedHeadRevision,
    }: {
      automationId: string;
      expectedHeadRevision: number;
    }) => {
      const { data } = await api.post<DirectRestorePublicationPreview>(
        `/automations/${automationId}/activation-restore-preview`,
        { expected_head_revision: expectedHeadRevision },
      );
      if (
        data.plan?.schema_version !== 'publication_plan.v2' ||
        data.plan.action !== 'rollback' ||
        data.plan.release !== null ||
        data.plan.targets.length !== 1 ||
        data.plan.targets[0].before.automation_id !== automationId ||
        !data.preview_token ||
        !data.preview_digest
      )
        throw new Error('Restore preview does not confirm the current Automation.');
      return data;
    },
  });
}

export function useDirectVersionPublication() {
  const api = useApiClient();
  const client = useQueryClient();
  const scope = useCompositionScope();
  return useMutation({
    retry: false,
    mutationFn: async ({
      automationId,
      ...request
    }: DirectVersionPublicationRequest): Promise<DirectVersionPublicationResult> => {
      const { data } = await api.post<DirectVersionPublicationResult>(
        `/automations/${automationId}/direct-publication`,
        request,
      );
      if (
        data.target_kind !== 'version' ||
        data.automation_version_id !== request.expected_draft_version_id ||
        !data.activation_id ||
        !data.publication_id
      )
        throw new Error('Publication receipt does not confirm this version.');
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: scope }),
        client.invalidateQueries({ queryKey: AUTOMATIONS_KEY }),
        client.invalidateQueries({ queryKey: SUITE_KEYS.root(scope[2], scope[3], scope[1]) }),
      ]);
    },
  });
}

/** Full Markdown is fetched only when the human explicitly opens the documents. */
export function usePublicationDocuments(
  fences: import('@/types/executionCompositions.ts').Fence[],
  enabled: boolean,
) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQueries({
    queries: fences.map((fence) => ({
      queryKey: [
        ...scope,
        'publication-documents',
        fence.release_id,
        fence.identity.documentation_digest,
      ],
      enabled: Boolean(enabled && scope[3]),
      retry: false,
      staleTime: 0,
      refetchOnMount: 'always' as const,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const { data } = await api.get<
          import('@/types/automationReleases.ts').ReleaseDocumentDetail
        >(`/automation-releases/${fence.release_id}/documents`, { signal });
        if (
          data.release_id !== fence.release_id ||
          data.documentation_digest !== fence.identity.documentation_digest ||
          !Array.isArray(data.member_documents)
        )
          throw new Error('Documents do not confirm the exact selected Release.');
        return data;
      },
    })),
  });
}
